import {
  Component,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  forwardRef,
  computed,
  inject,
  signal,
  effect,
  input,
  output,
  HostListener,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { ScrollingModule, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { OverlayModule, CdkConnectedOverlay, ConnectedPosition, ConnectedOverlayPositionChange } from '@angular/cdk/overlay';
import { SoSelectItem, SoSelectKey } from '../interfaces/select.interfaces';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';
import { SoAiLoaderUI } from '../so-ai-loader/so-ai-loader.component';
import { SoInputActionButton } from '../so-input/so-input.interfaces';

@Component({
  selector: 'so-select-ui',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ScrollingModule, OverlayModule, SoButtonGradientUI, SoAiLoaderUI],
  templateUrl: './so-select.component.html',
  styleUrl: './so-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoSelectUI),
      multi: true
    }
  ]
})
export class SoSelectUI implements ControlValueAccessor, OnInit {
  // Signal-based inputs
  items = input.required<SoSelectItem[]>();
  label = input<string>('');
  placeholder = input<string>('Seleccionar una opción');
  disabled = input<boolean>(false);
  loading = input<boolean>(false);
  required = input<boolean>(false);
  disabledKeys = input<SoSelectKey[]>([]);
  
  // Styling inputs
  customStyle = input<string>('');
  labelStyle = input<string>('');
  displayValueStyle = input<string>('');
  displayValueClass = input<string>('');
  selectHeight = input<number>(36);
  
  // Config inputs
  itemSize = input<number>(48);
  panelMaxHeight = input<number>(288);
  addSelectOption = input<boolean>(false);
  isFilter = input<boolean>(false);
  isMultiSelect = input<boolean>(false);
  searchable = input<boolean>(false);
  emptyMessage = input<string>('No hay opciones disponibles');
  noResultsMessage = input<string>('No encontramos resultados');
  isWhiteBox = input<boolean>(false);
  display = input<'vertical' | 'horizontal'>('vertical');
  allowDeselect = input<boolean>(false);
  autoApply = input<boolean>(false);
  fullWidth = input<boolean>(false);
  clearable = input<boolean>(false);
  hideFilterSearch = input<boolean>(false);

  // Action buttons (inline, inside trigger — standard mode only)
  actionButtons = input<SoInputActionButton[]>([]);

  // Backward compatibility inputs (aliases)
  selectLabel = input<string>('');
  selectedItemId = input<SoSelectKey>(null);
  isLoading = input<boolean>(false);
  isDisabled = input<boolean>(false);
  requiredMark = input<boolean>(false);
  tooltipDisabledText = input<string>('');
  formControl = input<FormControl | null>(null);

  // Outputs
  selectionChange = output<SoSelectKey>();
  selectionText = output<string>();
  multiSelectionChange = output<SoSelectKey[]>();

  // ViewChild references
  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;
  @ViewChild(CdkConnectedOverlay) connectedOverlay?: CdkConnectedOverlay;
  @ViewChild('triggerRef') triggerRef?: ElementRef<HTMLElement>;

  // Internal signals
  protected internalValue = signal<SoSelectKey>(null);
  protected isOpen = signal<boolean>(false);
  protected activeIndex = signal<number>(-1);
  protected triggerWidth = signal<number>(0);
  protected searchTerm = signal<string>('');
  protected isAbove = signal<boolean>(false);
  
  // Multi-select signals
  protected selectedValues = signal<SoSelectKey[]>([]);
  protected tempSelectedValues = signal<SoSelectKey[]>([]);
  
  // Single-select filter signals (for apply button behavior)
  protected tempSingleValue = signal<SoSelectKey>(null);

  private lastExternalSelectedItemId = signal<string | null>(null);

  // Constants
  readonly maxVisibleItems = 6;
  readonly panelId = `so-select-panel-${Math.random().toString(36).slice(2)}`;
  
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
  ];

  // ControlValueAccessor
  private onChange: (value: SoSelectKey | SoSelectKey[]) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed values
  protected processedItems = computed(() => {
    const items = this.items() || [];
    if (this.addSelectOption() && items.length > 0) {
      return [{ key: '', value: 'Seleccionar' }, ...items];
    }
    return items;
  });

  protected displayItems = computed(() => {
    const items = this.processedItems();
    const search = this.searchTerm().toLowerCase();
    if (!search) return items;
    return items.filter(item => item.value.toLowerCase().includes(search));
  });

  protected currentDisplayItem = computed(() => {
    const value = this.internalValue();
    if (value === null || value === undefined) return null;
    const item = this.processedItems().find(i => this.normalizeKey(i.key) === this.normalizeKey(value));
    return item?.display ?? item?.value ?? null;
  });
  currentDisplayItemSafe(): string | null {
    const raw = this.internalValue();
    if (raw === null || raw === undefined) return null;

    const normalized = this.normalizeKey(raw);
    const item = this.processedItems().find(i => this.normalizeKey(i.key) === normalized);

    if (item) return item.display ?? item.value ?? null;
    return null;
  }

  protected effectiveLabel = computed(() => this.label() || this.selectLabel());
  protected effectiveDisabled = computed(() => this.disabled() || this.isDisabled());
  protected effectiveLoading = computed(() => this.loading() || this.isLoading());
  private injector = inject(Injector);
  private controlRequired = signal(false);
  protected effectiveRequired = computed(() => this.required() || this.requiredMark() || this.controlRequired());

  protected panelHeight = computed(() => {
    const totalItems = this.displayItems().length;
    if (totalItems === 0) return Math.min(this.itemSize(), this.panelMaxHeight());
    const visibleItems = Math.min(totalItems, this.maxVisibleItems);
    return Math.min(visibleItems * this.itemSize(), this.panelMaxHeight());
  });

  protected viewportOverflowY = computed(() => {
    const totalContentHeight = this.displayItems().length * this.itemSize();
    return totalContentHeight > this.panelHeight() ? 'auto' : 'hidden';
  });

  protected standardViewportHeight = computed(() => {
    const totalItems = this.displayItems().length;
    if (totalItems === 0) return 0;

    const searchHeight = this.searchable() && !this.hasNoItems() ? 72 : 0;
    const maxViewportHeight = Math.max(this.itemSize(), this.panelMaxHeight() - searchHeight);
    return Math.min(Math.min(totalItems, this.maxVisibleItems) * this.itemSize(), maxViewportHeight);
  });

  protected standardPanelHeight = computed(() => {
    const searchHeight = this.searchable() && !this.hasNoItems() ? 72 : 0;
    const contentHeight = this.hasNoItems() || this.hasNoSearchResults()
      ? 64
      : this.standardViewportHeight();
    return searchHeight + contentHeight;
  });

  protected standardViewportOverflowY = computed(() => {
    return this.displayItems().length * this.itemSize() > this.standardViewportHeight() ? 'auto' : 'hidden';
  });

  protected activeOptionId = computed(() => {
    return this.activeIndex() >= 0 ? this.getOptionId(this.activeIndex()) : null;
  });

  protected hasNoItems = computed(() => this.processedItems().length === 0);
  protected hasNoSearchResults = computed(() => !this.hasNoItems() && this.displayItems().length === 0);

  // Multi-select computed
  protected multiSelectDisplayText = computed(() => {
    const values = this.selectedValues();
    if (values.length === 0) return '';
    if (values.length === 1) {
      const item = this.processedItems().find(i => this.normalizeKey(i.key) === this.normalizeKey(values[0]));
      return item?.value || '';
    }
    return `${values.length} seleccionados`;
  });

  // Single-select filter computed (for temp selection)
  protected isTempSingleSelected = (key: SoSelectKey): boolean => {
    return this.normalizeKey(this.tempSingleValue()) === this.normalizeKey(key);
  };

  protected allSelected = computed(() => {
    const items = this.displayItems().filter(item => !this.shouldBeDisabled(item.key));
    const temp = this.tempSelectedValues();
    return items.length > 0 && items.every(item => temp.some(v => this.normalizeKey(v) === this.normalizeKey(item.key)));
  });

  protected someSelected = computed(() => {
    const items = this.displayItems().filter(item => !this.shouldBeDisabled(item.key));
    const temp = this.tempSelectedValues();
    const selectedCount = items.filter(item => temp.some(v => this.normalizeKey(v) === this.normalizeKey(item.key))).length;
    return selectedCount > 0 && selectedCount < items.length;
  });

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    queueMicrotask(() => {
      const control = ngControl?.control;
      if (control?.hasValidator) {
        this.controlRequired.set(control.hasValidator(Validators.required));
      }
    });
  }

  constructor() {
    effect(() => {
      this.processedItems();

      if (this.isMultiSelect()) return;

      const externalId = this.selectedItemId();
      const normalizedExternal = this.normalizeKey(externalId);
      if (normalizedExternal === this.lastExternalSelectedItemId()) return;

      this.lastExternalSelectedItemId.set(normalizedExternal);
      this.internalValue.set(externalId);
      this.ensureActiveIndex();
    }, {  });

    effect(() => {
      this.processedItems();
      if (this.isMultiSelect()) return;
      this.internalValue();
      this.ensureActiveIndex();
    }, {  });

    effect(() => {
      this.processedItems();
      if (this.isOpen()) {
        queueMicrotask(() => {
          this.viewport?.checkViewportSize();
          this.connectedOverlay?.overlayRef?.updatePosition();
        });
      }
    }, {  });
  }

  // ControlValueAccessor implementation
  writeValue(value: SoSelectKey | SoSelectKey[] | null): void {
    if (this.isMultiSelect()) {
      const values = Array.isArray(value) ? value : [];
      this.selectedValues.set(values);
      this.tempSelectedValues.set([...values]);
    } else {
      this.internalValue.set(value as SoSelectKey);
      this.ensureActiveIndex();
    }
  }

  registerOnChange(fn: (value: SoSelectKey | SoSelectKey[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled via input signal
  }

  // Public methods
  shouldBeDisabled(key: SoSelectKey): boolean {
    const disabledKeys = this.disabledKeys();
    return disabledKeys.some(k => this.normalizeKey(k) === this.normalizeKey(key));
  }

  normalizeKey(key: SoSelectKey): string | null {
    if (key === null || key === undefined) return null;
    return String(key);
  }

  getOptionId(index: number): string {
    return `${this.panelId}-option-${index}`;
  }

  trackByKey(_index: number, item: SoSelectItem): string | number | boolean {
    return item.key;
  }

  // Panel operations
  openPanel(): void {
    if (this.isOpen() || this.effectiveDisabled()) return;
    this.searchTerm.set('');
    this.isOpen.set(true);
    this.setTriggerWidth();
    this.ensureActiveIndex();
    queueMicrotask(() => {
      this.connectedOverlay?.overlayRef?.updatePosition();
    });
  }

  closePanel(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.searchTerm.set('');
  }

  onTriggerClick(): void {
    if (this.effectiveDisabled()) return;
    this.isOpen() ? this.closePanel() : this.openPanel();
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.effectiveDisabled()) return;

    const openCurrentPanel = () => {
      if (this.isMultiSelect()) this.openPanelMulti();
      else if (this.isFilter()) this.openPanelSingleFilter();
      else this.openPanel();
    };

    switch (event.key) {
      case 'Tab':
        this.closeCurrentPanel();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) openCurrentPanel();
        this.moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) openCurrentPanel();
        this.moveActiveIndex(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (!this.isOpen()) openCurrentPanel();
        this.setBoundaryActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        if (!this.isOpen()) openCurrentPanel();
        this.setBoundaryActiveIndex(-1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.isOpen()) {
          openCurrentPanel();
        } else {
          this.selectActiveItem();
        }
        break;
      case 'Escape':
        if (this.isOpen()) {
          event.preventDefault();
          if (this.isMultiSelect()) this.cancelMultiSelection();
          else if (this.isFilter()) this.cancelSingleSelection();
          else this.closePanel();
        }
        break;
    }
  }

  selectItem(item: SoSelectItem): void {
    if (this.shouldBeDisabled(item.key)) return;

    const currentValue = this.normalizeKey(this.internalValue());
    const itemKey = this.normalizeKey(item.key);

    if (this.allowDeselect() && currentValue === itemKey) {
      this.internalValue.set(null);
      this.onChange(null);
      this.onTouched();
      this.selectionChange.emit(null);
      this.selectionText.emit('');
    } else {
      this.internalValue.set(item.key);
      this.onChange(item.key);
      this.onTouched();
      this.selectionChange.emit(item.key);
      this.selectionText.emit(item.display ?? item.value);
    }
    this.closePanel();
  }

  // Single-select filter methods (with apply button)
  openPanelSingleFilter(): void {
    if (this.isOpen() || this.effectiveDisabled()) return;
    this.tempSingleValue.set(this.internalValue());
    this.searchTerm.set('');
    this.isOpen.set(true);
    this.setTriggerWidth();
    this.ensureActiveIndex();
  }

  selectTempSingleItem(item: SoSelectItem): void {
    if (this.shouldBeDisabled(item.key)) return;

    const currentTemp = this.normalizeKey(this.tempSingleValue());
    const itemKey = this.normalizeKey(item.key);

    if (this.allowDeselect() && currentTemp === itemKey) {
      this.tempSingleValue.set(null);
    } else {
      this.tempSingleValue.set(item.key);
    }

    if (this.autoApply()) {
      this.applySingleSelection();
    }
  }

  applySingleSelection(): void {
    const selectedKey = this.tempSingleValue();
    this.internalValue.set(selectedKey);
    this.onChange(selectedKey);
    this.onTouched();
    this.selectionChange.emit(selectedKey);
    
    const item = this.processedItems().find(i => this.normalizeKey(i.key) === this.normalizeKey(selectedKey));
    if (item) {
      this.selectionText.emit(item.display ?? item.value);
    }
    this.closePanel();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.effectiveDisabled() || !this.currentDisplayItemSafe()) return;

    this.tempSingleValue.set(null);
    this.internalValue.set(null);
    this.onChange(null);
    this.onTouched();
    this.selectionChange.emit(null);
    this.selectionText.emit('');
    this.closePanel();
    this.triggerRef?.nativeElement.focus();
  }

  cancelSingleSelection(): void {
    this.tempSingleValue.set(this.internalValue());
    this.searchTerm.set('');
    this.closePanel();
  }

  // Multi-select methods
  openPanelMulti(): void {
    if (this.isOpen() || this.effectiveDisabled()) return;
    this.tempSelectedValues.set([...this.selectedValues()]);
    this.searchTerm.set('');
    this.isOpen.set(true);
    this.setTriggerWidth();
    this.ensureActiveIndex();
  }

  isItemSelected(key: SoSelectKey): boolean {
    return this.tempSelectedValues().some(v => this.normalizeKey(v) === this.normalizeKey(key));
  }

  toggleItem(item: SoSelectItem): void {
    if (this.shouldBeDisabled(item.key)) return;

    const current = [...this.tempSelectedValues()];
    const index = current.findIndex(v => this.normalizeKey(v) === this.normalizeKey(item.key));
    
    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(item.key);
    }
    this.tempSelectedValues.set(current);
  }

  toggleSelectAll(): void {
    const items = this.displayItems().filter(item => !this.shouldBeDisabled(item.key));
    
    if (this.allSelected()) {
      const keysToRemove = items.map(item => this.normalizeKey(item.key));
      this.tempSelectedValues.set(
        this.tempSelectedValues().filter(v => !keysToRemove.includes(this.normalizeKey(v)))
      );
    } else {
      const current = [...this.tempSelectedValues()];
      items.forEach(item => {
        if (!this.isItemSelected(item.key)) {
          current.push(item.key);
        }
      });
      this.tempSelectedValues.set(current);
    }
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.ensureActiveIndex();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'Escape'].includes(event.key)) return;
    this.onTriggerKeydown(event);
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    this.closeCurrentPanel();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen() || event.defaultPrevented) return;
    this.onTriggerKeydown(event);
  }

  applyMultiSelection(): void {
    this.selectedValues.set([...this.tempSelectedValues()]);
    this.onChange(this.selectedValues());
    this.onTouched();
    this.multiSelectionChange.emit(this.selectedValues());
    this.closePanel();
  }

  cancelMultiSelection(): void {
    this.tempSelectedValues.set([...this.selectedValues()]);
    this.searchTerm.set('');
    this.closePanel();
  }

  onPositionChange(event: ConnectedOverlayPositionChange): void {
    this.isAbove.set(event.connectionPair.overlayY === 'bottom');
  }

  // Private methods
  private setTriggerWidth(): void {
    this.triggerWidth.set(this.triggerRef?.nativeElement.getBoundingClientRect().width ?? 0);
  }

  private moveActiveIndex(delta: number): void {
    const items = this.displayItems();
    if (!items.length) return;

    const firstEnabled = this.findFirstEnabledIndex();
    if (firstEnabled === -1) {
      this.activeIndex.set(-1);
      return;
    }

    let nextIndex = this.activeIndex();
    const maxAttempts = items.length;
    let attempts = 0;

    do {
      attempts++;
      nextIndex = nextIndex + delta;
      if (nextIndex < 0) nextIndex = items.length - 1;
      if (nextIndex >= items.length) nextIndex = 0;
    } while (attempts <= maxAttempts && this.shouldBeDisabled(items[nextIndex].key));

    if (this.shouldBeDisabled(items[nextIndex].key)) {
      this.activeIndex.set(-1);
      return;
    }

    this.activeIndex.set(nextIndex);
    this.scrollToActiveIndex();
  }

  private ensureActiveIndex(): void {
    const normalizedValue = this.normalizeKey(this.internalValue());
    const items = this.displayItems();

    if (normalizedValue === null) {
      this.activeIndex.set(this.findFirstEnabledIndex());
    } else {
      const foundIndex = items.findIndex(item => this.normalizeKey(item.key) === normalizedValue);
      if (foundIndex !== -1 && !this.shouldBeDisabled(items[foundIndex].key)) {
        this.activeIndex.set(foundIndex);
      } else {
        this.activeIndex.set(this.findFirstEnabledIndex());
      }
    }
    untracked(() => this.scrollToActiveIndex());
  }

  private findFirstEnabledIndex(): number {
    return this.displayItems().findIndex(item => !this.shouldBeDisabled(item.key));
  }

  private setBoundaryActiveIndex(direction: 0 | -1): void {
    const items = this.displayItems();
    const indexes = direction === 0 ? items.map((_, index) => index) : items.map((_, index) => items.length - index - 1);
    const next = indexes.find(index => !this.shouldBeDisabled(items[index].key));
    this.activeIndex.set(next ?? -1);
    this.scrollToActiveIndex();
  }

  private selectActiveItem(): void {
    const item = this.displayItems()[this.activeIndex()];
    if (!item || this.shouldBeDisabled(item.key)) return;
    if (this.isMultiSelect()) this.toggleItem(item);
    else if (this.isFilter()) this.selectTempSingleItem(item);
    else this.selectItem(item);
  }

  private closeCurrentPanel(): void {
    if (!this.isOpen()) return;
    if (this.isMultiSelect()) this.cancelMultiSelection();
    else if (this.isFilter()) this.cancelSingleSelection();
    else this.closePanel();
  }

  private scrollToActiveIndex(): void {
    if (!this.isOpen() || this.activeIndex() < 0) return;

    queueMicrotask(() => {
      this.viewport?.scrollToIndex(this.activeIndex(), 'smooth');
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isOpen()) return;
    this.setTriggerWidth();
    this.connectedOverlay?.overlayRef?.updatePosition();
  }
}
